class GigantiDetails extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    if (!this.details) return;

    this.summary = this.details.querySelector(':scope > summary');

    this.details.addEventListener('toggle', () => {
      if (this.details.open && this.details.hasAttribute('data-lock-scroll')) {
        document.documentElement.classList.add('navigation-open');
        this.setBackgroundInert(true);
        window.requestAnimationFrame(() => this.getFocusableElements()[0]?.focus({ preventScroll: true }));
      } else if (this.details.hasAttribute('data-lock-scroll')) {
        document.documentElement.classList.remove('navigation-open');
        this.setBackgroundInert(false);
      }
    });

    this.addEventListener('keydown', (event) => {
      if (!this.details.open) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDetails();
        return;
      }

      if (event.key !== 'Tab' || !this.details.hasAttribute('data-lock-scroll')) return;
      const focusable = this.getFocusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    this.querySelectorAll('[data-close-details]').forEach((control) => {
      control.addEventListener('click', () => this.closeDetails());
    });
  }

  disconnectedCallback() {
    this.setBackgroundInert(false);
    document.documentElement.classList.remove('navigation-open');
  }

  closeDetails() {
    this.details.open = false;
    this.summary?.focus({ preventScroll: true });
  }

  getFocusableElements() {
    const scope = this.details.querySelector('.mobile-nav__panel') || this.details;
    return [...scope.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary')]
      .filter((element) => !element.hidden && element.getClientRects().length > 0);
  }

  setBackgroundInert(enabled) {
    if (enabled) {
      if (this.inertTargets) return;
      const headerInner = this.closest('.site-header__inner');
      const headerSiblings = headerInner ? [...headerInner.children].filter((element) => element !== this) : [];
      const pageRegions = [document.querySelector('.announcement'), document.querySelector('main'), document.querySelector('.site-footer')];
      const targets = [...headerSiblings, ...pageRegions].filter(Boolean);
      this.inertTargets = targets.map((element) => ({ element, wasInert: element.hasAttribute('inert') }));
      this.inertTargets.forEach(({ element }) => element.setAttribute('inert', ''));
      return;
    }

    this.inertTargets?.forEach(({ element, wasInert }) => {
      if (!wasInert) element.removeAttribute('inert');
    });
    this.inertTargets = null;
  }
}

if (!customElements.get('giganti-details')) {
  customElements.define('giganti-details', GigantiDetails);
}

document.addEventListener('click', (event) => {
  document.querySelectorAll('giganti-details details[open][data-close-outside]').forEach((details) => {
    if (!details.parentElement.contains(event.target)) details.open = false;
  });
});

document.addEventListener('shopify:section:unload', () => {
  document.documentElement.classList.remove('navigation-open');
});

(() => {
  const HOVER_DELAY_MS = 180;
  const PREFETCH_LIMIT = 8;
  const BLOCKED_PARAMS = ['preview_theme_id', 'section_id', 'sections'];
  const prefetchedUrls = new Set();
  let hoverTimer;
  let hoverAnchor;

  const getConnection = () => (
    navigator.connection || navigator.mozConnection || navigator.webkitConnection
  );

  const isThemePreview = () => {
    if (window.Shopify?.designMode) return true;
    if (new URLSearchParams(window.location.search).has('preview_theme_id')) return true;
    const themeRole = window.Shopify?.theme?.role;
    return Boolean(themeRole && themeRole !== 'main');
  };

  const networkAllowsPrefetch = () => {
    const connection = getConnection();
    if (!connection) return true;
    if (connection.saveData) return false;
    return !['slow-2g', '2g'].includes(connection.effectiveType);
  };

  const getAnchor = (target) => (
    target instanceof Element ? target.closest('a[href]') : null
  );

  const normalizeUrl = (value) => {
    const url = new URL(value, window.location.href);
    url.hash = '';
    if (url.pathname.length > 1) url.pathname = url.pathname.replace(/\/+$/, '');
    url.searchParams.sort();
    return url;
  };

  const getEligibleUrl = (anchor) => {
    if (!anchor || isThemePreview() || !networkAllowsPrefetch()) return null;
    if (
      anchor.hasAttribute('download')
      || anchor.hasAttribute('data-no-prefetch')
      || anchor.getAttribute('aria-disabled') === 'true'
      || (anchor.target && anchor.target !== '_self')
      || anchor.relList.contains('external')
      || anchor.relList.contains('nofollow')
    ) return null;

    const url = normalizeUrl(anchor.href);
    if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol)) return null;

    const segments = url.pathname.split('/').filter(Boolean);
    const firstSegment = segments[0]?.toLowerCase();
    const hasLocalePrefix = /^[a-z]{2}(?:-[a-z]{2})?$/i.test(firstSegment || '');
    const routeSegment = (hasLocalePrefix ? segments[1] : firstSegment)?.toLowerCase();
    if (!['products', 'collections'].includes(routeSegment)) return null;
    if (BLOCKED_PARAMS.some((param) => url.searchParams.has(param))) return null;

    const currentUrl = normalizeUrl(window.location.href);
    return url.href === currentUrl.href ? null : url.href;
  };

  const prefetch = (anchor) => {
    if (prefetchedUrls.size >= PREFETCH_LIMIT) return;
    const url = getEligibleUrl(anchor);
    if (!url || prefetchedUrls.has(url)) return;
    prefetchedUrls.add(url);

    if (
      typeof HTMLScriptElement !== 'undefined'
      && typeof HTMLScriptElement.supports === 'function'
      && HTMLScriptElement.supports('speculationrules')
    ) {
      const rules = document.createElement('script');
      rules.type = 'speculationrules';
      rules.textContent = JSON.stringify({
        prefetch: [{ source: 'list', urls: [url], eagerness: 'immediate' }],
      });
      document.head.append(rules);
      return;
    }

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.append(link);
  };

  const cancelHover = () => {
    window.clearTimeout(hoverTimer);
    hoverTimer = undefined;
    hoverAnchor = undefined;
  };

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType && !['mouse', 'pen'].includes(event.pointerType)) return;
    const anchor = getAnchor(event.target);
    if (!anchor || anchor === getAnchor(event.relatedTarget)) return;

    cancelHover();
    hoverAnchor = anchor;
    hoverTimer = window.setTimeout(() => {
      prefetch(anchor);
      cancelHover();
    }, HOVER_DELAY_MS);
  }, { passive: true });

  document.addEventListener('pointerout', (event) => {
    const anchor = getAnchor(event.target);
    const remainsInsideAnchor = event.relatedTarget instanceof Node && anchor?.contains(event.relatedTarget);
    if (anchor !== hoverAnchor || remainsInsideAnchor) return;
    cancelHover();
  }, { passive: true });

  document.addEventListener('focusin', (event) => prefetch(getAnchor(event.target)));
  document.addEventListener('pointerdown', (event) => {
    const anchor = getAnchor(event.target);
    if (anchor === hoverAnchor) cancelHover();
    prefetch(anchor);
  }, { passive: true });
})();
